/**
 * src/components/spatial/SpatialObjectZoneLayer.tsx
 *
 * Per-zone fill-extrusion layer. Zones are polygonal partitions inside
 * a floor (e.g. "Reception", "Cafe", "Members lounge"). They sit at the
 * same vertical band as their parent floor but are slightly taller +
 * more opaque so they read as the dominant surface when the user picks
 * a floor.
 *
 * Behaviour:
 *   - Always rendered when the spatial subsystem is enabled and the
 *     floor selector matches (or no floor is selected).
 *   - Hidden in slice mode (the slice plate is the dominant surface).
 *   - Lifts with the floor when explode-mode is on so each floor's
 *     zones move with it.
 */

import React, { useEffect } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useSpatialObjects } from './SpatialObjectsContext';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_SOURCE_ID,
  buildExtrusionBaseExpr,
  buildExtrusionColorExpr,
  buildExtrusionHeightExpr,
  buildExtrusionOpacityExpr,
  buildKindFilter,
} from './spatialLayerUtils';
import { useStrictModeSafeLayer } from './useStrictModeSafeLayer';

export interface SpatialObjectZoneLayerProps {
  provider: SpatialMapProvider;
  sourceId?: string;
  layerId?: string;
  beforeId?: string;
}

const SpatialObjectZoneLayer: React.FC<SpatialObjectZoneLayerProps> = ({
  provider,
  sourceId = SPATIAL_SOURCE_ID,
  layerId  = SPATIAL_LAYER_IDS.zone,
  beforeId,
}) => {
  const { view, filters } = useSpatialObjects();

  useStrictModeSafeLayer({
    provider,
    layerId,
    beforeId,
    buildSpec: () => ({
      id:     layerId,
      type:   'fill-extrusion',
      source: sourceId,
      filter: buildKindFilter('zone', view, filters) as unknown[],
      paint:  {
        'fill-extrusion-base':    buildExtrusionBaseExpr(view, 'zone'),
        'fill-extrusion-height':  buildExtrusionHeightExpr(view, 'zone'),
        'fill-extrusion-color':   buildExtrusionColorExpr('zone'),
        'fill-extrusion-opacity': buildExtrusionOpacityExpr('zone'),
      },
    }),
  });

  useEffect(() => {
    if (!provider.hasLayer(layerId)) return;
    provider.setFilter(layerId, buildKindFilter('zone', view, filters) as unknown[]);
    provider.setPaintProperty(layerId, 'fill-extrusion-base',   buildExtrusionBaseExpr(view, 'zone'));
    provider.setPaintProperty(layerId, 'fill-extrusion-height', buildExtrusionHeightExpr(view, 'zone'));
  }, [provider, layerId, view, filters]);

  return null;
};

export default SpatialObjectZoneLayer;
